import React from 'react';
import Form from 'react-bootstrap/Form';
import styles from '../../../MockParameters.module.css';
import HelpTooltip from '../../../../../../../components/overlays/helpTooltip/HelpTooltip';
import InputGroup from 'react-bootstrap/InputGroup';
import BzFormControlWithHook from '../../../../../../../components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, ValidatorType } from '../../../../../../../hooks/useFormInput';
import i18n from '../../../../../../../i18n';
import { REGEX_VALID_HTTP_URL } from '../../../../../../../utils/regexUtils';
import { useTranslation } from 'react-i18next';
import CertificateSelector from '../../../../../../../components/certUploader/CertificateSelector';
import _ from 'lodash';
import {
  addCertificateToAssetBundle,
  CertAssignment,
  getSelectedKeysFromAssetBundle,
  updateConfigurablePropertyInBundle,
} from '../../../../../../../utils/AssetBundleUtils';
import { HttpRecorderConfig } from '../../../../../../../types/rest/mockServiceTypes';
import { SslConfig, SSlConfigMode } from '../../../SslConfiguration';

const HttpRecordingSettings = ({
  disabled,
  updateConfigHandler,
  httpRecorderConfigDto,
  isHttpRecording,
}: {
  disabled: boolean;
  updateConfigHandler: (key: string, value: any) => void;
  httpRecorderConfigDto: HttpRecorderConfig;
  isHttpRecording?: boolean;
}) => {
  const { t } = useTranslation('mockServices');

  const getMaxRequestSize = () => {
    return _.get(httpRecorderConfigDto, 'maxRequestSize', 0);
  };
  const getMaxRequestsPerSecond = () => {
    return _.get(httpRecorderConfigDto, 'maxRequestsPerSecond', 0);
  };

  const assetsBundle4Http = {
    assets: httpRecorderConfigDto?.assets ?? [],
    configKeyAssets: httpRecorderConfigDto?.configKeyAssets ?? [],
  };

  const selectedCertificateKeys4Http = getSelectedKeysFromAssetBundle(
    assetsBundle4Http,
    SslConfig.ONE_WAY,
    SSlConfigMode.CLIENT,
  );

  const redirectMappingInput = useFormInput(
    httpRecorderConfigDto.liveSystemUrl,
    (fn: CallbackFn) => {
      if (fn.isValid) {
        updateConfigHandler('liveSystemUrl', fn.value);
      }
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorLiveSystemEndpointEmpty'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      value: REGEX_VALID_HTTP_URL,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorLiveSystemEndpoint'),
    },
  );

  return (
    <div style={{ maxWidth: '450px' }}>
      <div>
        <label htmlFor="maxRequestSizeId">Max Messages Count</label>
        <Form.Control
          id={'maxRequestSizeId'}
          disabled={disabled}
          onChange={event => {
            updateConfigHandler('maxRequestSize', event.target.value);
          }}
          type={'number'}
          min={0}
          placeholder={'Enter max request side...'}
          defaultValue={getMaxRequestSize()}
        />
      </div>

      <div className={'mt-4'}>
        <label htmlFor="maxRequestsPerSecondId">Max Messages per Second</label>
        <Form.Control
          id={'maxRequestsPerSecondId'}
          disabled={disabled}
          onChange={event => {
            updateConfigHandler('maxRequestsPerSecond', event.target.value);
          }}
          type={'number'}
          min={0}
          placeholder={'Enter max request per second...'}
          defaultValue={getMaxRequestsPerSecond()}
        />
      </div>

      <div className={'mt-0'}>
        {/* Redirect Mapping */}
        <Form.Group className={'mt-3'} controlId={'id-redirect-mapping'}>
          <Form.Label>Target URL</Form.Label>
          <span id="redirectMappingHelp" className={styles.questionInTableHeader} title={t('common:showHelp')}>
            <HelpTooltip id={'redirect_mapping_help'} />
          </span>
          <InputGroup className="mb-3">
            <BzFormControlWithHook
              disabled={false}
              useFormInputHook={redirectMappingInput}
              placeholder="http://mylivesystemendpoint"
              required={false}
            />
          </InputGroup>
        </Form.Group>
        {/* ./Redirect Mapping */}
      </div>
      <div className={'mt-4'}>
        {/* Cert Selector */}
        <CertificateSelector
          allowUnSelect={true}
          type={'CERTIFICATE'}
          selectedCertificateId={selectedCertificateKeys4Http.certificateId}
          disabled={false}
          onRemoveCertificate={id => {
            const filteredAssets = _.filter(assetsBundle4Http.assets, asset => asset.assetId !== id);
            updateConfigHandler('certificate', { assets: filteredAssets });
          }}
          onSelectCertificate={(certId, certName) => {
            //-2 means go back to no selection (certificate of keystore)
            if (certId === -2) {
              updateConfigHandler('certificate', { assets: [], configKeyAssets: [] });
              return;
            }
            const cert: CertAssignment = {
              certId: certId,
              certificateName: certName,
              usageType: 'CLIENT_TRUSTSTORE_CERT',
            };
            const bundle = addCertificateToAssetBundle(assetsBundle4Http, cert);
            updateConfigHandler('certificate', bundle);
          }}
          allowConfigurationProperty={true}
          onUpdateConfigPropertyValue={value => {
            const updatedBundle = updateConfigurablePropertyInBundle(
              assetsBundle4Http,
              value,
              'CLIENT_TRUSTSTORE_CERT',
            );
            updateConfigHandler('certificate', updatedBundle);
          }}
          configPropertyValue={selectedCertificateKeys4Http.configKeyValue}
        />
      </div>
    </div>
  );
};

export default HttpRecordingSettings;
